import React from 'react'
import { useTranslation } from 'react-i18next'
import { Quadrant } from '../../../calculators/EnergyFlow'
import {
  energyPerYearUnit,
  formatPercent,
  powerUnit,
} from '../../../util/formatters'
import { Provider } from '../../../calculators/providers/common/Provider'
import { solarPanelSubCategories } from '../../../provider/result-categories'
import { getFilteredResultsFromProviders } from '../../../calculators/calculator-result-utility'
import { mapNamedResultsToRows } from './report-utility'
import { ReportTable } from './ReportTable'

type Props = {
  providers: Provider[]
}

export function ReportSolarPanelsAndBatteriesTable({
  providers,
}: Props): React.ReactElement {
  const { t } = useTranslation()

  const filtersWithResults = solarPanelSubCategories.map((filter) => ({
    ...filter,
    result: getFilteredResultsFromProviders(providers, filter),
  }))

  const collected = filtersWithResults.find(
    (filter) => filter.quadrant === Quadrant.FromEnvironment,
  )
  const collectedTotal = collected?.result.total ?? 0

  const namedResults = filtersWithResults.map(({ result, ...filter }) => ({
    name: t(filter.nameKey),
    result,
    otherValues: [
      {
        other:
          filter.quadrant === Quadrant.FromEnvironment
            ? ''
            : formatPercent(result.total / collectedTotal),
      },
    ],
    highlightRow: filter.quadrant === Quadrant.FromEnvironment,
  }))

  const rows = mapNamedResultsToRows(namedResults)

  const headerCells = [
    { firstRow: energyPerYearUnit },
    { firstRow: powerUnit },
    { firstRow: t('common.portion') },
  ]

  return (
    <ReportTable
      title={t('report.solarPanelsAndBatteries')}
      headerCells={headerCells}
      rows={rows}
    />
  )
}
